import { Box, Chip, type SxProps, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { DateTime } from "luxon";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useNurseOvertimeMap } from "../../hooks/useNurseOvertimeMap";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { useScheduleCacheEnabled } from "../../hooks/useScheduleWeekRules";
import { BADGE, INK, INK_BLACK, NEUTRAL, STATUS, STATUS_BADGE } from "../../theme/tokens";
import { getShiftDisplayDate } from "../../utils/dateUtils";
import { hasHrisIntegrationOfType } from "../../utils/hrisIntegrations";
import {
	type Nurse,
	type NurseInShift,
	NurseTrainingStatus,
	OvertimeBadgeType,
	Preferences,
	type ShiftRequest,
	type StaffTableNote,
	StaffTableNotes,
} from "../../utils/models";
import { shouldApplyNurseNotOnboardedVisibleFlag } from "../../utils/utilFunctions";
import { isShiftExcludedFromPpd } from "../../utils/wageUtils";
import { isAgencyNurse } from "../../utils/warningDialogs/WarningDialog";
import { activeRequestTooltip } from "./activeRequestTooltip";
import { formatOtBadgeHours, shouldShowOtBadge } from "./otBadge";

type NurseShiftBadgesProps = {
	shift: NurseInShift;
	chipSx?: SxProps;
	ignoreTraineeOrOrientation?: boolean;
	disableNotOnboardedBadge?: boolean;
	maxBadges?: number;
	isActual?: boolean;
	hasActiveReplaceRequest?: boolean;
	// The open replace request behind the "Active Request" badge; its close time goes in the tooltip.
	activeReplaceRequest?: ShiftRequest;
	showMissingBreakPunches?: boolean;
	canShowLastActualShiftBadge?: boolean;
};

const LONG_SHIFT_THRESHOLD_HOURS = 18;

export const NurseShiftBadges: FC<NurseShiftBadgesProps> = ({
	shift,
	chipSx,
	ignoreTraineeOrOrientation = false,
	disableNotOnboardedBadge = false,
	maxBadges,
	isActual = false,
	hasActiveReplaceRequest = false,
	activeReplaceRequest,
	showMissingBreakPunches = true,
	canShowLastActualShiftBadge = false,
}) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const { hospitalData, isHospitalDemo, isHospitalView, getNurseById, chosenDepartment, getWeekRange, timezone } = useHospitalData();
	const showHospitalView = isHospitalView || isHospitalDemo;
	const hasShiftsIntegration = hasHrisIntegrationOfType(chosenDepartment, "shifts");
	const logicalDate = getShiftDisplayDate(shift.startTime, chosenDepartment.dayStart, chosenDepartment.shift_delta_minutes);
	const { startDate: otStartDate, endDate: otEndDate } = getWeekRange(logicalDate);
	const { data: otMap } = useNurseOvertimeMap(chosenDepartment.department_id, otStartDate, otEndDate, timezone, isActual);
	const showMissingBreaksBadge = useFeatureFlag(FEATURE_FLAGS.ShowMissingBreaksBadge);
	const showLongShiftsBadge = useFeatureFlag(FEATURE_FLAGS.LongShiftsBadge);
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const isCacheEnabled = useScheduleCacheEnabled();

	const nurse = useMemo<Nurse | undefined>(() => getNurseById(shift.nurseId), [getNurseById, shift.nurseId]);

	const notes = useMemo<StaffTableNote[]>(() => {
		const notes: StaffTableNote[] = [];
		let lastShiftBadge: StaffTableNote | null = null;

		// Show "Last Shift" badge for scheduled shifts when nurse hasn't worked recently
		if (canShowLastActualShiftBadge && hasShiftsIntegration) {
			// With the cache on, the roster's newest punch in this department (no week list to search); off, the newest
			// actual shift in the old list's weeks, as before.
			const lastActualShiftStart = isCacheEnabled
				? nurse?.lastPunchTime
				: hospitalData.weeks
						.flatMap((week) =>
							week.weeklyShifts.flatMap((shiftType) => shiftType.days.flatMap((day) => (day.nursesActual || []).filter((n) => n.nurseId === shift.nurseId))),
						)
						.sort((a, b) => b.startTime.valueOf() - a.startTime.valueOf())[0]?.startTime;

			if (lastActualShiftStart && nurse) {
				const totalDays = shift.startTime.diff(lastActualShiftStart, "days").days;

				// Calculate PTO days in this period (excluding time off from working days count)
				const timeOffDays = nurse.employeeSpecificPreferences
					.filter((pref) => {
						if (pref.type !== Preferences.Avoid) return false;
						return pref.end_date > lastActualShiftStart && pref.start_date < shift.startTime;
					})
					.reduce((sum, pref) => {
						const overlapStart = pref.start_date > lastActualShiftStart ? pref.start_date : lastActualShiftStart;
						const overlapEnd = pref.end_date < shift.startTime ? pref.end_date : shift.startTime;
						const days = overlapEnd.diff(overlapStart, "days").days;
						return sum + Math.max(0, days);
					}, 0);

				const workingDays = totalDays - timeOffDays;

				// Show badge if working days > 14
				if (workingDays > 14) {
					const formattedDate = lastActualShiftStart.toFormat("MM/dd");
					lastShiftBadge = {
						text: t("badge.last.shift", { date: formattedDate }),
						color: STATUS_BADGE.WARN_BG,
						textColor: STATUS_BADGE.WARN_INK,
					};
				}
			}
		}

		if (hasActiveReplaceRequest) {
			notes.push({
				...StaffTableNotes.ActiveRequest,
				text: t("badge.active.request"),
				tooltip: activeRequestTooltip(activeReplaceRequest, timezone, t, DateTime.now()),
			});
		}

		// These go through `t` for the same reason the OT badges below do. DAT-803 makes the punch-out
		// badge load-bearing — it is now the only thing marking a nurse the grid still counts as staff —
		// so leaving it as a hardcoded English string would strand that meaning for es/he users.
		if (shift.missingPunchIn && shift.missingPunchOut) {
			notes.push({ ...StaffTableNotes.Pending, text: t("badge.pending") });
		} else if (shift.missingPunchIn) {
			notes.push({ ...StaffTableNotes.Pending, text: t("badge.pending") });
			notes.push({ ...StaffTableNotes.MissingPunchIn, text: t("badge.missing.punch.in") });
		} else if (shift.missingPunchOut) {
			notes.push({ ...StaffTableNotes.Pending, text: t("badge.pending") });
			notes.push({ ...StaffTableNotes.MissingPunchOut, text: t("badge.missing.punch.out") });
		}

		if (isActual && showMissingBreaksBadge && showMissingBreakPunches && !shift.breaks?.length && hasShiftsIntegration) {
			notes.push(StaffTableNotes.MissingBreakPunches);
		}

		const isTrainee = shift.trainingStatus === NurseTrainingStatus.Trainee;
		const isOrientation = shift.trainingStatus === NurseTrainingStatus.Orientation;
		const isEducation = shift.trainingStatus === NurseTrainingStatus.Education;
		if (isTrainee && !ignoreTraineeOrOrientation) notes.push(StaffTableNotes.Trainee);
		if (isOrientation && !ignoreTraineeOrOrientation) notes.push(StaffTableNotes.Orientation);
		if (isEducation && !ignoreTraineeOrOrientation) notes.push(StaffTableNotes.Education);

		try {
			if (!nurse) return notes;

			if (isAgencyNurse(shift.type)) {
				notes.push({
					text: nurse.agencyName || "Agency",
					color: StaffTableNotes.Agency.color,
					textColor: StaffTableNotes.Agency.textColor,
				});
			} else {
				const dayKey = logicalDate.toISODate();
				const overtimeInfo = dayKey ? otMap?.[shift.nurseId]?.[dayKey] : undefined;
				// All four badge labels go through `t`. They were English template literals, so a
				// Spanish or Hebrew user saw "Consecutive OT 8.0h" sitting in an otherwise translated
				// grid. Translating only the new consecutive one would have left the row bilingual,
				// which reads worse than uniformly-English — hence all four in one change.
				if (overtimeInfo && shouldShowOtBadge(overtimeInfo.hours)) {
					const overtimeHours = { hours: formatOtBadgeHours(overtimeInfo.hours, isMobile) };
					if (overtimeInfo.type === OvertimeBadgeType.Weekly) {
						notes.push({
							...StaffTableNotes.WeeklyOT,
							text: t("badge.weekly.ot", overtimeHours),
						});
					} else if (overtimeInfo.type === OvertimeBadgeType.Daily) {
						notes.push({
							...StaffTableNotes.DailyOTHigh,
							text: t("badge.daily.ot", overtimeHours),
						});
					} else if (overtimeInfo.type === OvertimeBadgeType.Consecutive) {
						notes.push({
							...StaffTableNotes.ConsecutiveOT,
							text: t("badge.consecutive.ot", overtimeHours),
						});
					} else if (overtimeInfo.type === OvertimeBadgeType.Approaching) {
						notes.push({
							...StaffTableNotes.ApproachingOT,
							text: t("badge.approaching.ot", overtimeHours),
						});
					}
				}
			}

			if (!disableNotOnboardedBadge && shouldApplyNurseNotOnboardedVisibleFlag(nurse)) notes.push(StaffTableNotes.NotOnboarded);
			if (isShiftExcludedFromPpd(jobManagementEnabled, shift.excludeFromPpd, nurse.excludeFromPpd)) notes.push(StaffTableNotes.ExcludedFromPpd);
		} catch {}

		const shiftLength = shift.endTime.diff(shift.startTime, "hours").as("hours");
		if (showHospitalView && (shiftLength === 4 || shiftLength === 8))
			notes.push({
				text: `${shiftLength} hours`,
				color: STATUS_BADGE.BRAND_FALLBACK,
				textColor: "white",
			});

		if (isActual && showLongShiftsBadge && shiftLength > LONG_SHIFT_THRESHOLD_HOURS) {
			notes.push({ text: t("badge.long.shift", { hours: Math.round(shiftLength) }), color: NEUTRAL[100], textColor: STATUS.ERROR.dark });
		}

		if (shift.bonus && shift.bonus > 0) {
			const amount = Number.isInteger(shift.bonus) ? `${shift.bonus}` : shift.bonus.toFixed(2);
			notes.push({ ...StaffTableNotes.Incentive, text: t("badge.incentive", { amount }) });
		}

		// Add "Last Shift" badge last
		if (lastShiftBadge) {
			notes.push(lastShiftBadge);
		}

		return maxBadges ? notes.slice(0, maxBadges) : notes;
	}, [
		shift,
		hospitalData,
		showHospitalView,
		nurse,
		ignoreTraineeOrOrientation,
		disableNotOnboardedBadge,
		maxBadges,
		hasActiveReplaceRequest,
		activeReplaceRequest,
		timezone,
		isActual,
		showMissingBreaksBadge,
		hasShiftsIntegration,
		showMissingBreakPunches,
		showLongShiftsBadge,
		jobManagementEnabled,
		t,
		canShowLastActualShiftBadge,
		otMap,
		logicalDate,
		isMobile,
		isCacheEnabled,
	]);

	const getOvertimeTooltip = (note: StaffTableNote): React.JSX.Element | string | null => {
		try {
			const dayKey = logicalDate.toISODate();
			const overtimeInfo = dayKey ? otMap?.[shift.nurseId]?.[dayKey] : undefined;
			if (!overtimeInfo?.shifts?.length) return null;

			if (note.text.startsWith("Weekly OT") || note.text.startsWith("Daily OT") || note.text.startsWith("Approaching OT")) {
				const tooltipShifts = overtimeInfo.shifts
					.map((s) => {
						const start = DateTime.fromISO(s.startTime);
						const end = DateTime.fromISO(s.endTime);
						const hours = end.diff(start, "hours").as("hours").toFixed(1);
						const day = start.toFormat("ccc, MMM d");
						const isSameDept = s.departmentDisplayName === chosenDepartment.display_name;
						return {
							day,
							start: start.toFormat("hh:mm a"),
							end: end.toFormat("hh:mm a"),
							hours,
							departmentName: isSameDept ? null : s.departmentDisplayName,
							sortKey: start.valueOf(),
						};
					})
					.sort((a, b) => a.sortKey - b.sortKey);

				// Single grid for all rows so columns align even when only some shifts
				// have a non-default departmentName. The 4th column is always reserved.
				const hasAnyDeptName = tooltipShifts.some((s) => s.departmentName);
				const gridTemplateColumns = hasAnyDeptName ? "1.5fr 2fr 0.8fr 1.5fr" : "1.5fr 2fr 1fr";
				return (
					<Box sx={{ display: "grid", gridTemplateColumns, columnGap: 1, rowGap: 1 }}>
						{tooltipShifts.flatMap((s) => [
							<Typography key={`${s.day}-${s.start}-day`} sx={{ fontSize: "12px" }}>
								{s.day}
							</Typography>,
							<Typography key={`${s.day}-${s.start}-time`} sx={{ fontSize: "12px" }}>
								{`${s.start}-${s.end}`}
							</Typography>,
							<Typography key={`${s.day}-${s.start}-hours`} sx={{ fontSize: "12px" }}>
								{s.hours}h
							</Typography>,
							hasAnyDeptName ? (
								<Typography key={`${s.day}-${s.start}-dept`} sx={{ fontSize: "12px", color: "text.secondary" }}>
									{s.departmentName ?? ""}
								</Typography>
							) : null,
						])}
					</Box>
				);
			}
		} catch {}
		return null;
	};

	return (
		<Box sx={{ display: "flex", gap: 0.5, alignItems: "center", flexWrap: "wrap" }}>
			{notes.map((note, index) => {
				const tooltip = getOvertimeTooltip(note) ?? note.tooltip ?? null;

				return tooltip ? (
					<Tooltip
						key={`${note.text}-${index}`}
						title={
							<Typography
								sx={{
									whiteSpace: "pre-line",
									color: INK.SECONDARY,
									fontSize: "14px",
								}}
							>
								{tooltip}
							</Typography>
						}
						arrow
						placement="top"
						slotProps={{
							tooltip: {
								sx: {
									bgcolor: "white",
									border: "1px solid white",
									boxShadow: `0px 4px 12px ${alpha(INK_BLACK, 0.1)}`,
									width: "fit-content",
									maxWidth: typeof tooltip === "string" ? BADGE.TEXT_TOOLTIP_MAX_WIDTH : "none",
									"& .MuiTooltip-arrow": {
										color: "white",
										"&::before": {
											border: "1px solid white",
										},
									},
								},
							},
						}}
					>
						<Chip
							label={note.text}
							size="small"
							sx={{
								backgroundColor: note.color,
								color: note.textColor ?? "white",
								fontSize: {
									xs: 8,
									mediumPhone: 10,
									sm: 10,
								},
								fontWeight: 600,
								height: 20,
								"& .MuiChip-label": { px: 1 },
								...chipSx,
							}}
						/>
					</Tooltip>
				) : (
					<Chip
						key={`${note.text}-${index}`}
						label={note.text}
						size="small"
						sx={{
							backgroundColor: note.color,
							color: note.textColor ?? "white",
							fontWeight: 600,
							fontSize: {
								xs: 10,
								mediumPhone: 10,
								sm: 10,
							},
							height: 20,
							"& .MuiChip-label": { px: 1 },
							...chipSx,
						}}
					/>
				);
			})}
		</Box>
	);
};
